<template>
    <Card :title="$t('cardTitles.productsSettings')">
        <div class="card-body">
            <Fieldset :title="$t('labels.price_templates')">
                <div class="row">
                    <div class="col-4">
                        <FormGroup :label="$t('labels.default')">
                            <InputGroup>
                                <Autocomplete
                                    :model-value="priceTemplate?.uuid"
                                    @update:model-value="selectPriceTemplate"
                                    :selected-value="priceTemplate?.name"
                                    :url="priceTemplatesUrl"
                                    :can-be-empty="true"
                                />
                                <template #append>
                                    <Button size="sm"
                                        color="indigo"
                                        @click="createAndSubstitutePriceTemplate('priceTemplate', integration.service.charAt(0).toUpperCase() + this.integration.service.slice(1) + ` (${this.integration.name})`)"
                                        :disabled="priceTemplate !== null">
                                        {{ $t('buttons.create') }}
                                    </Button>
                                </template>
                            </InputGroup>
                        </FormGroup>
                    </div>
                    <div class="col-4">
                        <FormGroup :label="$t('labels.with_discount')">
                            <InputGroup>
                                <Autocomplete
                                    :model-value="salePriceTemplate?.uuid"
                                    @update:model-value="(optionValue) => salePriceTemplate = optionValue"
                                    :selected-value="salePriceTemplate?.name"
                                    :url="priceTemplatesUrl"
                                    :disabled="!priceTemplate?.uuid"
                                    :can-be-empty="true"
                                />
                                <template #append>
                                    <Button size="sm"
                                        color="indigo"
                                        @click="createAndSubstitutePriceTemplate('salePriceTemplate', integration.service.charAt(0).toUpperCase() + this.integration.service.slice(1) + ` (${this.integration.name}) with discount`)"
                                        :disabled="priceTemplate && salePriceTemplate">
                                        {{ $t('buttons.create') }}
                                    </Button>
                                </template>
                            </InputGroup>
                        </FormGroup>
                    </div>
                    <div class="col-4">
                        <FormGroup :label="$t('labels.promoPrice')">
                            <InputGroup>
                                <Autocomplete
                                    :model-value="promoPriceTemplate?.uuid"
                                    @update:model-value="(optionValue) => promoPriceTemplate = optionValue"
                                    :selected-value="promoPriceTemplate?.name"
                                    :url="priceTemplatesUrl"
                                    :disabled="!priceTemplate?.uuid"
                                    :can-be-empty="true"
                                />
                                <template #append>
                                    <Button size="sm"
                                        color="indigo"
                                        @click="createAndSubstitutePriceTemplate('promoPriceTemplate', integration.service.charAt(0).toUpperCase() + this.integration.service.slice(1) + ` (${this.integration.name}) with promo`)"
                                        :disabled="priceTemplate && salePriceTemplate">
                                        {{ $t('buttons.create') }}
                                    </Button>
                                </template>
                            </InputGroup>
                        </FormGroup>
                    </div>
                    <slot v-for="dropshipper in dropshippers">
                        <div class="col-4">
                            <FormGroup :label="$t('labels.dropshipper_name', {name: dropshipper.name})" >
                                <InputGroup>
                                    <Autocomplete
                                        :model-value="dropshipperPriceTemplates?.[dropshipper.uuid]?.price_template_uuid"
                                        @update:model-value="selectDropshipperPriceTemplate($event, dropshipper.uuid)"
                                        :selected-value="selectedPriceTemplates[dropshipperPriceTemplates?.[dropshipper.uuid]?.price_template_uuid]?.name"
                                        :url="priceTemplatesUrl"
                                        :can-be-empty="true"
                                    />
                                    <template #append>
                                        <Button size="sm"
                                            color="indigo"
                                            @click="createAndSubstituteDropshipperPriceTemplate(dropshipper.uuid, 'price_template_uuid', dropshipper.name)">
                                            {{ $t('buttons.create') }}
                                        </Button>
                                    </template>
                                </InputGroup>
                            </FormGroup>
                        </div>
                        <div class="col-4">
                            <FormGroup :label="$t('labels.with_discount_dropshipper_name', {name: dropshipper.name})">
                                <InputGroup>
                                    <Autocomplete
                                        :model-value="dropshipperPriceTemplates?.[dropshipper.uuid]?.sale_price_template_uuid"
                                        @update:model-value="(optionValue) => updateDropshipperSalePriceTemplate(dropshipper.uuid, optionValue)"
                                        :selected-value="selectedPriceTemplates[dropshipperPriceTemplates?.[dropshipper.uuid]?.sale_price_template_uuid]?.name"
                                        :url="priceTemplatesUrl"
                                        :disabled="!dropshipperPriceTemplates?.[dropshipper.uuid]?.price_template_uuid"
                                        :can-be-empty="true"
                                    />
                                    <template #append>
                                        <Button size="sm"
                                            color="indigo"
                                            @click="createAndSubstituteDropshipperPriceTemplate(dropshipper.uuid, 'sale_price_template_uuid', dropshipper.name + ' with discount')"
                                            :disabled="dropshipperPriceTemplates?.[dropshipper.uuid]?.price_template_uuid === undefined && dropshipperPriceTemplates?.[dropshipper.uuid]?.sale_price_template_uuid === undefined">
                                            {{ $t('buttons.create') }}
                                        </Button>
                                    </template>
                                </InputGroup>
                            </FormGroup>
                        </div>
                        <div class="col-4">
                            <FormGroup :label="$t('labels.with_promo_dropshipper_name', {name: dropshipper.name})">
                                <InputGroup>
                                    <Autocomplete
                                        :model-value="dropshipperPriceTemplates?.[dropshipper.uuid]?.promo_price_template_uuid"
                                        @update:model-value="(optionValue) => updateDropshipperPromoPriceTemplate(dropshipper.uuid, optionValue)"
                                        :selected-value="selectedPriceTemplates[dropshipperPriceTemplates?.[dropshipper.uuid]?.promo_price_template_uuid]?.name"
                                        :url="priceTemplatesUrl"
                                        :disabled="!dropshipperPriceTemplates?.[dropshipper.uuid]?.price_template_uuid"
                                        :can-be-empty="true"
                                    />
                                    <template #append>
                                        <Button size="sm"
                                            color="indigo"
                                            @click="createAndSubstituteDropshipperPriceTemplate(dropshipper.uuid, 'promo_price_template_uuid', dropshipper.name + ' with promo')"
                                            :disabled="dropshipperPriceTemplates?.[dropshipper.uuid]?.price_template_uuid === undefined && dropshipperPriceTemplates?.[dropshipper.uuid]?.promo_price_template_uuid === undefined">
                                            {{ $t('buttons.create') }}
                                        </Button>
                                    </template>
                                </InputGroup>
                            </FormGroup>
                        </div>
                    </slot>
                </div>
            </Fieldset>
            <Fieldset :title="$t('labels.automatic_update_fields')">
                <div class="row">
                    <div class="col-lg-3" v-for="field in ProductFieldsForUpdateEnum">
                        <FormGroup :label="$t(`labels.${field === 'filterItems' ? 'filter_items' : field}`)">
                            <CheckBox :modelValue="productFieldsForUpdate.includes(field)" @update:modelValue="(checked) => selectProductFieldForUpdate(field, checked)"/>
                        </FormGroup>
                    </div>
                </div>
            </Fieldset>
            <Fieldset :title="$t('labels.fields_for_export')" v-if="integrationServicesForExportFields.includes(integration.service)">
                <div class="row">
                    <slot v-for="field in FieldsForExportEnum">
                        <div class="col-lg-4">
                            <FormGroup :label="$t(`labels.${field}`)">
                                <CheckBox :modelValue="fieldsForExport.includes(field)" @update:modelValue="(checked) => selectFieldForExport(field, checked)"/>
                            </FormGroup>
                        </div>
                    </slot>
                </div>
            </Fieldset>
            <Fieldset :title="$t('labels.additional')">
                <div class="row">
                    <div class="col-md-3">
                        <FormGroup :label="$t('labels.autoExportGeneration')">
                            <CheckBox v-model="autoExportGeneration"/>
                        </FormGroup>
                    </div>
                    <div class="col-md-3" v-if="integration.service === 'prom' || integration.service === 'kasta'">
                        <FormGroup :label="$t('labels.without_product_grouping')">
                            <CheckBox v-model="withoutProductGrouping"/>
                        </FormGroup>
                    </div>
                    <div class="col-md-3" v-if="integration.service === 'prom'">
                        <FormGroup :label="$t('labels.marketplaceRegions')">
                            <MultiAutocomplete
                                :url="`/projects/${$route.params.projectUuid}/marketplace/sources/${integration.service}/regions`"
                                :model-value="regions"
                                @update:model-value="(newRegions) => regions = newRegions"
                                optionValue="name_uk"
                            />
                        </FormGroup>
                    </div>
                </div>
            </Fieldset>
        </div>
        <div class="card-footer d-flex justify-content-end">
            <Button size="sm" @click="save" color="indigo">{{ $t('buttons.save') }}</Button>
        </div>
    </Card>
</template>
<script>
import Card from '../../../../../js/components/new/CardComponent.vue';
import Fieldset from '../../../../../js/components/new/AppFieldset.vue';
import FormGroup from '../../../../../js/components/new/form/group/FormGroup.vue';
import Button from '../../../../../js/components/new/button/AppButton.vue';
import CheckBox from '../../../../../js/components/new/form/checkbox/AppCheckbox.vue';
import InputGroup from '../../../../components/new/form/InputGroup.vue';
import Autocomplete from '../../../../../js/components/new/form/autocomplete/AppAutoComplete.vue';
import MultiAutocomplete from '../../../../../js/components/new/form/autocomplete/MultiAutoComplete.vue';

import { ProductFieldsForUpdateEnum } from '../..../../../../enums/ProductFieldsForUpdateEnum';
import FieldsForExportEnum from '../..../../../../enums/FieldsForExportEnum.ts';
import { httpClient } from '../../../../services/HttpClient.ts';
import { mapGetters } from 'vuex';

export default {
    name: "ProductsCard",
    components: {
        Card, Fieldset, FormGroup, Button, CheckBox, InputGroup, Autocomplete, MultiAutocomplete
    },
    props: {
        integration: {
            type: Object,
            required: true
        },
    },
    emits: ['update:integration'],
    data() {
        return {
            ProductFieldsForUpdateEnum,
            FieldsForExportEnum,
            withoutProductGrouping: this.integration.product_settings.without_product_grouping ?? false,
            priceTemplate: null,
            salePriceTemplate: null,
            promoPriceTemplate: null,
            productFieldsForUpdate: this.integration.product_settings.fields_for_update ?? [],
            fieldsForExport: this.integration.product_settings.fields_for_export ?? [],
            dropshipperPriceTemplates: this.integration?.dropshipper_price_templates || {},
            integrationServicesForExportFields: ['prom', 'horoshop'],
            selectedPriceTemplates: {},
            dropshippers: [],
            autoExportGeneration: this.integration.product_settings.auto_export_generation ?? false,
            regions: [],
        }
    },
    methods: {
        fetchCurrentRegions() {
            if (!this.integration.product_settings.region_uuids?.length) return

            httpClient.get(`/projects/${this.$route.params.projectUuid}/marketplace/sources/${this.integration.service}/regions`, {
                uuids: this.integration.product_settings.region_uuids
            }).then(response => {
                this.regions = response.data.data
            })
        },
        selectPriceTemplate(optionValue) {
            this.priceTemplate = optionValue
            if (optionValue === null) {
                this.salePriceTemplate = null
                this.promoPriceTemplate = null
            }
        },
        selectDropshipperPriceTemplate(optionValue, dropshipperUuid) {
            this.dropshipperPriceTemplates[dropshipperUuid] ??= {}

            this.rememberPriceTemplate(optionValue)
            this.dropshipperPriceTemplates[dropshipperUuid].price_template_uuid = optionValue ? optionValue.uuid : null

            if (optionValue === null) {
                this.dropshipperPriceTemplates[dropshipperUuid].sale_price_template_uuid = null
                this.dropshipperPriceTemplates[dropshipperUuid].promo_price_template_uuid = null
            }
        },
        selectProductFieldForUpdate(field, checked) {
            if (checked) {
                this.productFieldsForUpdate.push(field)
            } else {
                this.productFieldsForUpdate = this.productFieldsForUpdate.filter(f => f !== field)
            }
        },
        selectFieldForExport(field, checked) {
            if (checked) {
                this.fieldsForExport.push(field)
            } else {
                this.fieldsForExport = this.fieldsForExport.filter(f => f !== field)
            }
        },
        save() {
            let request = {
                product_settings: {
                    title_formula: null,
                    price_formula: null,
                    fields_for_update: this.productFieldsForUpdate,
                    auto_export_generation: this.autoExportGeneration,
                    price_template_uuid: this.priceTemplate ? this.priceTemplate.uuid : null,
                    sale_price_template_uuid: this.salePriceTemplate ? this.salePriceTemplate.uuid : null,
                    promo_price_template_uuid: this.promoPriceTemplate ? this.promoPriceTemplate.uuid : null,
                    region_uuids: this.regions.map(region => region.uuid)
                }
            }

            if (Object.values(this.dropshipperPriceTemplates).length) {
                request.dropshipper_price_templates = Object.entries(this.dropshipperPriceTemplates)
                    .filter(([dropshipper_uuid, {price_template_uuid, sale_price_template_uuid, promo_price_template_uuid}]) => {
                        return price_template_uuid !== null
                    })
                    .map(([dropshipper_uuid, {price_template_uuid, sale_price_template_uuid, promo_price_template_uuid}]) => {
                        let dropshipperPriceTemplate = {
                            dropshipper_uuid,
                            price_template_uuid: price_template_uuid || null,
                            sale_price_template_uuid: sale_price_template_uuid || null,
                            promo_price_template_uuid: promo_price_template_uuid || null
                        }

                        return dropshipperPriceTemplate
                    })
            }

            if (this.integration.service === 'prom' || this.integration.service === 'kasta') {
                request.product_settings.without_product_grouping = this.withoutProductGrouping;
            }

            if (this.integrationServicesForExportFields.includes(this.integration.service)) {
                request.product_settings.fields_for_export = this.fieldsForExport;
            }

            this.$emit('update:integration', request)
        },
        async fetchSelectedPriceTemplates() {
            const uuids = [
                this.integration.product_settings.price_template_uuid,
                this.integration.product_settings.sale_price_template_uuid,
                this.integration.product_settings.promo_price_template_uuid,
                ...Object.values(this.dropshipperPriceTemplates).flatMap((templates) => [
                    templates.price_template_uuid,
                    templates.sale_price_template_uuid,
                    templates.promo_price_template_uuid
                ])
            ].filter(Boolean)

            if (!uuids.length) return

            await httpClient.get(this.priceTemplatesUrl, {
                uuids: [...new Set(uuids)],
                limit: 0
            }).then((response) => {
                response.data.data.forEach((template) => this.rememberPriceTemplate(template))
            })
        },
        rememberPriceTemplate(priceTemplate) {
            if (!priceTemplate) return

            this.selectedPriceTemplates[priceTemplate.uuid] = {
                uuid: priceTemplate.uuid,
                name: priceTemplate.name
            }
        },
        async fetchDropshippers() {
            await httpClient.get('/dropshippers', {
                customer_project_uuids: [this.project.uuid],
                limit: 0,
                extra: 1
            }).then(response => {
                this.dropshippers = response.data.data
            })
        },
        createAndSubstitutePriceTemplate(priceTemplate, name) {
            httpClient.post(this.priceTemplatesUrl, {
                name: name,
                rounding: false
            }).then((response) => {
                this[priceTemplate] = response.data.data
            })
        },
        createAndSubstituteDropshipperPriceTemplate(dropshipperUuid, type, name) {
            httpClient.post(this.priceTemplatesUrl, {
                name: name,
                rounding: false
            }).then((response) => {
                if (this.dropshipperPriceTemplates[dropshipperUuid] === undefined) {
                    this.dropshipperPriceTemplates[dropshipperUuid] = {}
                }

                this.dropshipperPriceTemplates[dropshipperUuid][type] = response.data.data.uuid

                this.rememberPriceTemplate(response.data.data)
            })
        },
        updateDropshipperSalePriceTemplate(dropshipperUuid, optionValue) {
            if (!this.dropshipperPriceTemplates[dropshipperUuid]) {
                this.dropshipperPriceTemplates[dropshipperUuid] = {};
            }
            
            if (optionValue) {
                this.rememberPriceTemplate(optionValue);
                this.dropshipperPriceTemplates[dropshipperUuid].sale_price_template_uuid = optionValue.uuid;
            } else {
                delete this.dropshipperPriceTemplates[dropshipperUuid].sale_price_template_uuid;
            }
        },
        updateDropshipperPromoPriceTemplate(dropshipperUuid, optionValue) {
            if (!this.dropshipperPriceTemplates[dropshipperUuid]) {
                this.dropshipperPriceTemplates[dropshipperUuid] = {};
            }

            if (optionValue) {
                this.rememberPriceTemplate(optionValue);
                this.dropshipperPriceTemplates[dropshipperUuid].promo_price_template_uuid = optionValue.uuid;
            } else {
                delete this.dropshipperPriceTemplates[dropshipperUuid].promo_price_template_uuid;
            }
        }
    },
    computed: {
        priceTemplatesUrl() {
            return `/projects/${this.$route.params.projectUuid}/pim/price-templates`;
        },
        ...mapGetters({
            project: 'route/project',
        }),
    },
    async mounted() {
        await this.fetchDropshippers()
        this.fetchCurrentRegions()

        this.dropshipperPriceTemplates = this.integration.dropshippers_price_templates.reduce((acc, template) => {
            acc[template.dropshipper_uuid] = {
                price_template_uuid: template.price_template_uuid,
                sale_price_template_uuid: template.sale_price_template_uuid,
                promo_price_template_uuid: template.promo_price_template_uuid,
            }

            return acc
        }, {})

        await this.fetchSelectedPriceTemplates()

        this.priceTemplate = this.selectedPriceTemplates[this.integration.product_settings.price_template_uuid] ?? null
        this.salePriceTemplate = this.selectedPriceTemplates[this.integration.product_settings.sale_price_template_uuid] ?? null
        this.promoPriceTemplate = this.selectedPriceTemplates[this.integration.product_settings.promo_price_template_uuid] ?? null
    },
}
</script>
